import { Injectable, signal, untracked } from '@angular/core'
import {
   Shift,
   Doctor,
   VetSchedulePlannerClient,
   VSPShift_Type,
   VSPDoctor_Type,
   EmployeeInfo,
   Week,
   Day,
   VSPDay_Type,
   VSPWeek_Status,
   PracticesClient,
   PracticeOfficeHours,
} from '../shared/services/ppv.api'
import { format } from 'date-fns'
import { forkJoin, Observable, of, switchMap, tap } from 'rxjs'
import * as datefns from 'date-fns'
import { ToastrService } from 'ngx-toastr'
import { ConfirmationService } from 'primeng/api'

export type DayUI = {
   startTimeDate?: Date
   endTimeDate?: Date
   type?: VSPDay_Type
   shifts?: Shift[]
}

@Injectable({
   providedIn: 'root',
})
export class VetSchedulePlannerService {
   isLoading = signal(false)
   week = signal<Week | null>(null)
   weekList = signal<Week[] | null>(null)
   selectedWeekInfo = signal<Week | null>(null)
   weekStartDate = signal<Date | null>(null)
   dayDialogVisible = signal(false)
   addDoctorDialogVisible = signal(false)
   doctorInfoDialogVisible = signal(false)
   inactiveDoctorListDialogVisible = signal(false)
   inactiveDoctors = signal<{ lendahand: Doctor[]; relief: Doctor[]; employee: Doctor[] }>({ lendahand: [], relief: [], employee: [] })
   doctorInfo = signal<EmployeeInfo | null>(null)
   selectedDay = signal<Day | null>(null)
   selectedDoctor = signal<Doctor | null>(null)
   minTime = new Date('2000-01-01T00:00:00')
   maxTime = new Date('2000-01-01T23:59:59')
   isWorkDay = signal(false)
   weekdays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
   doctorTotals = signal<{ [doctorId: number]: number }>({})
   doctorDays = signal<{ [doctorId: number]: number }>({})
   dayTotals = signal<{ [dayIndex: number]: number }>({})
   isDoctorAllWeekOff = signal<{ [doctorId: number]: boolean }>({})
   isReadyToSubmit = signal(false)
   isPrevWeek = signal(false)
   isReadyToConfirm = signal(false)
   officeHours = signal<PracticeOfficeHours | null>(null)

   constructor(
      private vspClient: VetSchedulePlannerClient,
      private practicesClient: PracticesClient,
      private toastr: ToastrService,
      private confirmationService: ConfirmationService
   ) {}

   showError(message: string) {
      this.toastr.error(message)
      this.confirmationService.confirm({
         header: 'Error',
         message: message,
         acceptLabel: 'OK',
         rejectVisible: false,
         accept: () => {},
      })
   }

   loadOfficeHours(practiceSKey: number) {
      this.practicesClient.getOfficeHours(practiceSKey).subscribe({
         next: (hours) => this.officeHours.set(hours),
         error: () => this.officeHours.set(null),
      })
   }

   doesDoctorExist(doctor): Observable<boolean> {
      return this.vspClient.doesDoctorExists(doctor)
   }

   getExportData(start: string, count: number) {
      return this.vspClient.getDataForExport(start, count)
   }

   getWeeklyHoursReport() {
      return this.vspClient.getWeeklyHoursReport()
   }

   reloadWeek() {
      this.loadWeek(this.week()!.practiceSKey, this.weekStartDate()!)
   }

   loadWeek(practiceSKey: number, weekStart: Date) {
      this.weekStartDate.set(weekStart)
      this.isLoading.set(true)
      if (untracked(() => this.officeHours()?.practiceSKey) !== practiceSKey) {
         this.loadOfficeHours(practiceSKey)
      }
      this.vspClient.getWeekInfo(practiceSKey, datefns.format(weekStart, 'yyyy-MM-dd')).subscribe({
         next: (weekInfo) => {
            console.log('Loaded week:', weekInfo)
            this.week.set(weekInfo.requestedWeek)
            this.weekList.set(
               weekInfo.weekList.map((w) => ({
                  ...w,
                  startEndString: datefns.format(w.firstDay, 'MMM. d') + ' - ' + datefns.format(datefns.addDays(w.firstDay, 6), 'MMM. d'),
               }))
            )
            console.log('Week list:', this.weekList())
            this.selectedWeekInfo.set(
               this.weekList().find((w) => datefns.format(w.firstDay, 'yyyy-MM-dd') === datefns.format(weekStart, 'yyyy-MM-dd')) || null
            )
            this.refreshCalculated()
            this.toastr.success('Data loaded successfully.')
         },
         error: () => {
            this.isLoading.set(false)
            this.showError('Failed to load data.')
         },
         complete: () => {
            this.isLoading.set(false)
         },
      })
   }

   refreshCalculated() {
      let totals: { [doctorId: number]: number } = {}
      let doctorDaysCount: { [doctorId: number]: number } = {}
      let dayTotals: { [dayIndex: number]: number } = {}
      let isAllWeekOff: { [doctorId: number]: boolean } = {}
      let i = 0
      for (let doctor of this.week()!.doctors) {
         let totalMinutes = 0
         doctorDaysCount[i] = 0
         isAllWeekOff[i] = true
         for (let day of doctor.days) {
            if (day.type !== VSPDay_Type.ScheduledDayOff && !this.week().isDayClosed[day.weekday]) {
               isAllWeekOff[i] = false
            }
            if (day.type === VSPDay_Type.Working && !this.week().isDayClosed[day.weekday]) {
               for (let shift of day.shifts || []) {
                  if (shift.startTime && shift.endTime) {
                     let start = datefns.parseISO('2000-01-01T' + shift.startTime)
                     let end = datefns.parseISO('2000-01-01T' + shift.endTime)
                     let diff = datefns.differenceInMinutes(end, start)
                     totalMinutes += diff
                     dayTotals[day.weekday] = (dayTotals[day.weekday] || 0) + diff / 60.0
                  }
               }
               doctorDaysCount[i] = (doctorDaysCount[i] || 0) + 1
            }
         }
         totals[i++] = totalMinutes / 60
      }
      this.isDoctorAllWeekOff.set(isAllWeekOff)
      this.doctorDays.set(doctorDaysCount)
      this.doctorTotals.set(totals)
      this.isReadyToSubmit.set(this.getIsReadyToSubmit())
      this.isPrevWeek.set((this.week().firstDay as any).substring(0, 10) === (this.weekList()[0].firstDay as any).substring(0, 10))
      this.isReadyToConfirm.set((this.week().status === VSPWeek_Status.Submitted || this.isReadyToSubmit()) && this.isPrevWeek())
      console.log('Calculated doctor totals:', totals)
      console.log('Is ready to submit:', this.isReadyToSubmit())
      console.log('Is previous week:', this.isPrevWeek())
      console.log('Is ready to confirm:', this.isReadyToConfirm())
      console.log('Day totals:', dayTotals)
      this.dayTotals.set(dayTotals)
   }

   getIsReadyToSubmit() {
      let week = this.week()
      if (!week) return false
      if (week.status !== VSPWeek_Status.Incomplete) return false
      for (let doctor of week.doctors) {
         for (let day of doctor.days) {
            if (!week.isDayClosed[day.weekday] && !day.type) {
               return false
            }
         }
      }
      return true
   }

   showInactiveDoctorList() {
      this.isLoading.set(true)
      this.vspClient.listInactiveDoctors(this.week()!.practiceSKey).subscribe({
         next: (doctors) => {
            this.inactiveDoctors.set({
               lendahand: doctors.filter((d) => d.type === VSPDoctor_Type.LendAHand),
               relief: doctors.filter((d) => d.type === VSPDoctor_Type.Relief),
               employee: doctors.filter((d) => d.type === VSPDoctor_Type.Employee),
            })
            this.inactiveDoctorListDialogVisible.set(true)
         },
         error: () => {
            this.isLoading.set(false)
            this.showError('Failed to load inactive doctors.')
         },
         complete: () => {
            this.isLoading.set(false)
         },
      })
   }

   searchActiveDfDoctors(query: string) {
      return this.vspClient.searchActiveDfDoctors(query)
   }

   clearDay(doctor: Doctor, day: Day) {
      this.isLoading.set(true)
      this.selectedDoctor.set(doctor)
      this.selectedDay.set(day)
      this.saveDayChanges({ type: VSPDay_Type.NotSet, shifts: null }).subscribe({
         next: () => {
            this.toastr.success('Day cleared successfully.')
         },
         complete: () => {
            this.isLoading.set(false)
         },
         error: () => {
            this.showError('Failed to clear day.')
            this.isLoading.set(false)
         },
      })
   }

   markDoctorAllWeek(doctor: Doctor, type: VSPDay_Type) {
      this.isLoading.set(true)
      this.selectedDoctor.set(doctor)
      this.selectedDay.set(null)

      let weekId = this.week().id ? of(this.week().id) : this.saveWeekChanges()
      let doctorId = doctor.id ? of(doctor.id) : this.saveDoctorChanges(doctor, doctor)
      forkJoin([weekId, doctorId])
         .pipe(
            switchMap(() => {
               let updates: Observable<any>[] = []
               doctor.days.forEach((day) => {
                  if (day.type !== type && !this.week().isDayClosed[day.weekday]) {
                     updates.push(
                        this.saveDayChanges({
                           id: day.id,
                           type: type,
                           shifts: null,
                           weekday: day.weekday,
                        }).pipe(
                           tap((id) => {
                              day.id = id
                              day.type = type
                              day.shifts = null
                           })
                        )
                     )
                  }
               })
               return forkJoin(updates)
            })
         )
         .subscribe({
            next: () => {
               this.week().status = VSPWeek_Status.Incomplete
               this.refreshCalculated()
               this.toastr.success('Changes saved successfully.')
            },
            complete: () => {
               this.isLoading.set(false)
            },
            error: (e) => {
               this.isLoading.set(false)
               this.showError('Failed to save changes.')
            },
         })
   }

   showDoctorInfoDialog(doctor: Doctor) {
      this.selectedDoctor.set({ ...doctor })
      this.doctorInfo.set({} as EmployeeInfo)
      if (doctor.type === VSPDoctor_Type.Employee || doctor.type === VSPDoctor_Type.LendAHand) {
         this.isLoading.set(true)
         this.vspClient.getDoctorInfo(doctor.dayforceId!).subscribe({
            next: (dfInfo) => {
               this.doctorInfo.set(dfInfo)
               this.doctorInfoDialogVisible.set(true)
            },
            error: () => {
               this.showError('Failed to load doctor information.')
               this.isLoading.set(false)
            },
            complete: () => {
               this.isLoading.set(false)
            },
         })
      } else {
         this.doctorInfoDialogVisible.set(true)
      }
   }

   showAddDoctorDialog() {
      this.selectedDoctor.set({} as Doctor)
      this.addDoctorDialogVisible.set(true)
   }

   copyWeek(dst: string) {
      this.isLoading.set(true)
      return this.vspClient.copyWeek(this.week().id, dst).pipe(tap(() => this.isLoading.set(false)))
   }

   setWeekdayClosed(dayIndex: number, isClosed: boolean) {
      let toSave = { ...this.week() }
      toSave.isDayClosed = [...(toSave.isDayClosed || [false, false, false, false, false, false, false])]
      toSave.isDayClosed[dayIndex] = isClosed
      toSave.closedDays = toSave.isDayClosed
         .map((closed, index) => (closed ? index : -1))
         .filter((index) => index >= 0)
         .join(',')
      this.isLoading.set(true)
      this.saveWeekChanges(toSave).subscribe({
         next: () => {
            this.week().isDayClosed[dayIndex] = isClosed
            if (isClosed) {
               this.week().doctors.forEach((doctor) => {
                  doctor.days = doctor.days.map((d) => {
                     if (d.weekday === dayIndex) {
                        return { ...d, id: 0, type: VSPDay_Type.NotSet, shifts: null }
                     }
                     return d
                  })
               })
            }
         },
         complete: () => {
            this.isLoading.set(false)
         },
         error: (err) => {
            this.week().isDayClosed[dayIndex] = !isClosed
            this.isLoading.set(false)
            this.showError('Failed to update day status.')
         },
      })
   }

   openDayDialog(day: Day, doctor: Doctor, isWorking: boolean = false) {
      this.isWorkDay.set(isWorking)
      this.selectedDay.set(day)
      this.selectedDoctor.set(doctor)
      this.dayDialogVisible.set(true)
   }

   timeFormats = ['hh:mm a', 'hh:mma', 'hha', 'hh a']

   timeSpanStringToDate(str: string) {
      return new Date('2000-01-01 ' + str)
   }

   timeFormat(str) {
      return format(new Date('2000-01-01 ' + str), 'h:mm a')
   }

   saveWeekChanges(changes: Week | null = null) {
      if (changes == null) {
         changes = { ...this.week() }
         delete changes.doctors
      }
      return this.vspClient.saveWeek(changes).pipe(
         tap((id) => {
            console.log('Week saved successfully')
            this.week().status = this.week().closedDays === changes.closedDays ? changes.status : VSPWeek_Status.Incomplete
            if (this.week().id) {
               this.weekList().find((w) => w.id === id)!.status = this.week().status
            } else {
               this.week().id = id
               let fd = this.week().firstDay
               let wld = this.weekList().find((w) => w.firstDay === fd)
               if (wld) {
                  wld.id = id
                  wld.status = this.week().status
               }
            }
            this.week().closedDays = changes.closedDays
            this.week().isDayClosed = changes.isDayClosed
            this.refreshCalculated()
         })
      )
   }

   saveDoctorChanges(changes: Doctor | null = null, selectedDoctor: Doctor | null = null) {
      selectedDoctor = selectedDoctor || this.selectedDoctor()
      if (changes == null) {
         changes = { ...selectedDoctor }
         delete changes.days
      }
      return this.vspClient.saveDoctor(changes).pipe(
         tap((id) => {
            console.log('Doctor saved successfully')
            if (changes) {
               selectedDoctor.id = id
               selectedDoctor.type = changes.type
               selectedDoctor.agencyName = changes.agencyName
               selectedDoctor.dayforceId = changes.dayforceId
               selectedDoctor.firstName = changes.firstName
               selectedDoctor.lastName = changes.lastName
               selectedDoctor.doesNotWorkHere = changes.doesNotWorkHere
               this.refreshCalculated()
            }
         })
      )
   }

   saveDayChanges(changes: Partial<Day>, selectedDoctor: Doctor | null = null, selectedDay: Day | null = null) {
      selectedDoctor = selectedDoctor || this.selectedDoctor()
      selectedDay = selectedDay || this.selectedDay()
      let doctorId = selectedDoctor.id ? of(selectedDoctor.id) : this.saveDoctorChanges(selectedDoctor, selectedDoctor)
      let weekId = this.week().id ? of(this.week().id) : this.saveWeekChanges()

      return forkJoin([doctorId, weekId]).pipe(
         switchMap(([doctorId, weekId]) => {
            let toSave = {
               ...selectedDay,
               ...changes,
               doctorId: doctorId,
               weekId: weekId,
            }
            console.log('Saving day:', toSave)
            return this.vspClient.saveDay(this.week().practiceSKey, toSave).pipe(
               tap((id) => {
                  console.log('Day saved successfully')
                  if (!!selectedDay) {
                     selectedDay.id = id
                     selectedDay.type = toSave.type
                     selectedDay.shifts = toSave.shifts
                     this.week().status = VSPWeek_Status.Incomplete
                     this.refreshCalculated()
                  }
               })
            )
         })
      )
   }
}
